import type { Project } from "@/src/api/ProjectAPI.types";
import type { GetBandwidthResponse } from "@/src/api/StatsAPI.types";
import type { OrganizationOptions } from "@/src/types/organization.types";

import type { BandwidthKey, QuotaUsage } from "./usage.types";
import type { ChartSeries, TimelineDatum } from "../charts/charts.types";

export const QUOTA_CRITICAL_RATIO = 0.9;

export function buildBandwidthTimeline(
  response: GetBandwidthResponse | undefined,
): TimelineDatum<BandwidthKey>[] {
  return (response?.bandwidth_stats ?? []).map((bandwidthStat) => ({
    date: bandwidthStat.date,
    values: {
      vod: bandwidthStat.vod_bw ?? 0,
      live: bandwidthStat.live_bw ?? 0,
      fast: bandwidthStat.fast_bw ?? 0,
    },
  }));
}

export function sumBandwidth(
  response: GetBandwidthResponse | undefined,
): number {
  return (response?.bandwidth_stats ?? []).reduce(
    (sum, bandwidthStat) => sum + bandwidthStat.bandwidth,
    0,
  );
}

export function getQuotaUsage(used: number, max: number): QuotaUsage | null {
  if (max <= 0) return null;

  const ratio = used / max;
  return {
    ratio,
    remaining: Math.max(0, max - used),
    overage: Math.max(0, used - max),
    isCritical: ratio >= QUOTA_CRITICAL_RATIO,
  };
}

export function getBandwidthSeriesList(
  options:
    | Pick<OrganizationOptions, "allow_live_okast" | "allow_fast">
    | undefined,
  translate: (key: string) => string,
): ChartSeries<BandwidthKey>[] {
  const seriesList: ChartSeries<BandwidthKey>[] = [
    {
      key: "vod",
      label: translate("usage.series.vod"),
      color: "var(--chart-1)",
    },
  ];
  if (options?.allow_live_okast) {
    seriesList.push({
      key: "live",
      label: translate("usage.series.live"),
      color: "var(--chart-2)",
    });
  }
  if (options?.allow_fast) {
    seriesList.push({
      key: "fast",
      label: translate("usage.series.fast"),
      color: "var(--chart-3)",
    });
  }
  return seriesList;
}

export function sumProjectUsers(
  projectList: readonly Pick<Project, "active_users" | "registered_users">[],
): { activeUserCount: number; registeredUserCount: number } {
  return projectList.reduce(
    (sum, project) => ({
      activeUserCount: sum.activeUserCount + project.active_users,
      registeredUserCount: sum.registeredUserCount + project.registered_users,
    }),
    { activeUserCount: 0, registeredUserCount: 0 },
  );
}
